// src/Skeletons/IncomingInventorySkeleton.jsx
import React, { useMemo, useRef } from 'react'
import {
  Box, Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TableSortLabel, useMediaQuery, useTheme,
} from '@mui/material'
import { CONTENT_WIDTH, FS, SP, TABELLE_ZEILENHOEHE_PX } from '../theme/tokens'
import Panel from '../components/common/Panel'
import KpiRow, { KpiCell } from '../components/common/KpiRow'
import { PAGE_GUTTER } from '../components/common/PageContainer'
import useColumnPriority from '../components/common/useColumnPriority'
import useContainerWidth from '../components/common/useContainerWidth'
import {
  INCOMING_INVENTORY_DEFAULT_COLUMNS,
  incomingInventoryLayout,
  incomingInventoryMinWidth,
  incomingInventoryPriorities,
} from '../incomingInventoryColumns'

// §6 PR 1 / Schritt 1.2 (Plan 2026-10-06-ux-ladezustaende), Runde 4:
// die Zeilen sind `TableRow`/`TableCell` in einer `<Table>` mit DERSELBEN Spaltenzahl
// wie die geladene Tabelle — die Spaltenliste kommt aus DERSELBEN Quelle
// (`incomingInventoryColumns.js`, gelesen von `IncomingInventory.jsx` `renderTableHead`).
// Die vorige Fassung zaehlte die Spalten nur und zeichnete Balken in die Kopfzelle; damit
// war die Kopfzeile flacher als die geladene, sobald ein Etikett umbrach (gemessen
// 2026-10-06: 39 px im Ladezustand gegen 59 px geladen, R15 W1 / R16 W1).
const ROWS = 8

// Die Zeilenbox einer Typografie-Variante — genau die Hoehe, die eine geladene Zeile
// belegt. 🔴 MUI setzt fuer `variant="text"` sonst `1.2em`, und das ist FLACHER als die
// Zeilenbox (body2 16,8 gegen 20,0 px; caption 14,4 gegen 19,9 px): die Datenzeile waere
// im Ladezustand kuerzer als die geladene und spraenge beim Eintreffen der Daten
// (R15 W1 / R16 W1). Die Hoehe kommt aus dem Theme, nicht aus einer px-Zahl.
function lineBoxHeight(theme, variant) {
  const { fontSize, lineHeight } = theme.typography[variant] || {}
  return typeof lineHeight === 'number' ? `calc(${lineHeight} * ${fontSize})` : lineHeight
}

/** Die Zeilenbox einer GELADENEN Datenzelle in DIESER Tabelle.
 *
 *  🔴 **Der Unterschied ist gemessen, nicht geschaetzt (G8b-Nachbesserung 2,
 *  2026-10-22).** Die geladene Tabelle steht in `TableViewport`
 *  (`stickyFirstColumn`), und dessen `stickySx` setzt
 *  `td:not([colspan]) { line-height: TABELLE_ZEILENHOEHE_PX }` samt `td *`
 *  (`TableViewport.jsx:522-527`). Die Textzeile einer geladenen Datenzelle ist
 *  dort also **16 px** hoch, nicht 17,52 (`body2` 1,43 × 12,25) bzw. 17,67
 *  (`caption`). Das Geruest steht NICHT in `TableViewport` (es muss seinen
 *  Behälter ungedeckelt messen) und zeichnete darum die Typografie-Zeilenbox:
 *  vier Balken = 87,375 px gegen einen geladenen Median von 82 px (gemessen im
 *  A/B 2026-10-22). Vier Balken à 16 px = 64 + 16 Polster + 1 Rahmen = **81 px**
 *  — der Median liegt bei 82, die Abweichung bei 1 px.
 *
 *  🔴 Warum eine feste Hoehe die Regel nicht erbt: `line-height` ueberschreibt
 *  keine `height`. Der Balken traegt seine Hoehe im `sx`, also muss er sie aus
 *  DERSELBEN Quelle nehmen wie die geladene Textzeile — `TABELLE_ZEILENHOEHE_PX`
 *  ist genau dieser Token (er existiert fuer `TableViewport.stickySx` und nennt
 *  sich „die ganzzahlige Zeilenhoehe einer Tabelle mit klebender erster
 *  Spalte"). Eine zweite Zahl waere eine zweite Quelle. */
const ZEILENBOX_TABELLE = TABELLE_ZEILENHOEHE_PX

/** Die Aussenhoehe einer `Pill` in `size="sm"` — aus ihren drei Bestandteilen
 *  (`components/common/Pill.jsx`), nicht als px-Zahl:
 *
 *    Zeilenbox  `FS.sm` × 1,4   (der `lineHeight`, den die Pill setzt)
 *  + Polster     `py: 0.5` je Seite — MUI-Spacing, also `SP.chip`
 *  + Rahmen      1 px je Seite (die Pill reserviert ihn immer, auch transparent)
 *
 *  🔴 **G8b-Nachbesserung 4 (2026-10-22):** die fünf Etiketten dieser Seite
 *  sind von MUI `Chip` (18 px, `ETIKETT_KOMPAKT_SX`) auf `Pill` umgezogen. Das
 *  Geruest zeichnete weiter 18 px und waere beim Eintreffen der Daten um die
 *  Pill-Hoehe gesprungen. Die Hoehe wird deshalb hier HERGELEITET, nicht
 *  nachgetragen: eine eigene Zahl waere eine zweite Quelle fuer dieselbe
 *  Geometrie, und die naechste Aenderung an `Pill` liesse sie stehen.
 *
 *  🔴 **R16 W2 (G8b-Nachbesserung 5, 2026-10-23) — die drei Ziffern stehen in
 *  `Pill.jsx`, nicht hier.** Diese Formel ist eine Abschrift von drei Werten,
 *  die die Pill selbst setzt: `py` (`Pill.jsx:207`), `lineHeight` (`:211`) und
 *  `border` (`:224`). Wer dort einen davon aendert, aendert die WIRKLICHE
 *  Hoehe — diese Formel steht still, und ohne eine Probe bliebe der Sprung
 *  beim Laden stillschweigend zurueck. Die Ziffern sind Wegweiser, nicht
 *  Quelle; `grep -n "lineHeight\|border: '1px" frontend/src/components/common/Pill.jsx`
 *  findet sie am heutigen Stand.
 *
 *  Gebunden ist die Kopplung von **L21b** (`Skeletons/ladezustaende.test.jsx`):
 *  sie rendert eine echte `Pill`, liest Zeilenhoehe, Polster und Rahmen aus
 *  ihrem `getComputedStyle` und haelt die Summe gegen DIESE Funktion. Wer
 *  `Pill.jsx` anfasst, zieht Formel und L21 im selben Commit nach. */
export function pillHoeheSm(theme) {
  const polster = parseFloat(theme.spacing(SP.chip))
  return FS.sm * 1.4 + 2 * polster + 2
}

// Die Zeilen der geladenen ersten Zelle (Produkt / Variante / SKU,
// `IncomingInventory.jsx` `renderDataRow`): body2 mit fontWeight 600, darunter zweimal
// caption. Dieselben Varianten hier — daraus folgt die Hoehe der Zelle.
//
// 🔴 Welle 2 PR 3a (2026-10-06): die Produktzeile ist ZWEIZEILIG — auf dem Desktop.
// Gemessen (Orchestrator, 1440): Geruestzeile 70 px gegen geladene Zeile 88 px. Die
// geladene Zelle traegt den Produktnamen OHNE `noWrap` (`IncomingInventory.jsx:534`,
// seit G8a eingerueckt — deshalb kein byte-identischer Nachzug, die Aussage traegt dort),
// er bricht in der atmenden `product_name`-Spalte auf zwei Zeilen um; das Geruest zeichnete
// nur EINE. Eine Zeile misst hier 18 px (aus 88 - 70 bei 3 gegen 4 Zeilen), genau die
// fehlende Zeile.
//
// 🔴 `nameLines={2}` ist das MAXIMUM, nicht der Regelfall (R16 H2). Ein KURZER
// Produktname laedt EINZEILIG, und dann ist die geladene Zelle 18 px niedriger als
// das Geruest — der Resthoehensprung ist hier nur nach oben abgedeckt. Das Geruest
// kennt die Namenslaenge nicht und waehlt bewusst den Rand, an dem die Mehrheit
// nicht springt; ein Mittelwert waere keine Verbesserung, sondern eine zweite Zahl
// ohne Quelle.
//
// 🔴 **G8b-Nachbesserung 2 (2026-10-22): die ZEILENBOX der Tabelle kommt aus
// `TABELLE_ZEILENHOEHE_PX`, nicht aus der Typografie** — siehe
// `ZEILENBOX_TABELLE`. Die geladene Tabelle steht in `TableViewport`, dessen
// `stickySx` jeder Datenzelle `line-height: 16px` setzt; ein Balken mit fester
// `height` erbt das nicht und war darum 6,375 px zu hoch (87,375 gegen 82 im
// A/B). Die Kartenform ist nicht betroffen und misst weiter die Typografie.
//
// 🔴 **Seit G8b (R16 W3, 2026-10-22) ist auch die MOBILFASSUNG ZWEIZEILIG:** die
// geladene `MobileRecordCard` klemmt den Produktnamen auf zwei Zeilen
// (`-webkit-line-clamp: 2`, `IncomingInventory.jsx` `KARTE_NAME_SX`). Deshalb ruft
// die mobile Kartenform `nameLines={2}`. Die Zeilenzahl bleibt ein Prop und keine
// Konstante — eine Ansicht mit einzeiligem Titel braucht weiter die Vorgabe 1.
function FirstCellSkeleton({ theme, nameLines = 1, zeilenHoehe }) {
  // `zeilenHoehe` ist die Hoehe, die die GELADENE Zelle an dieser Stelle belegt
  // (Tabelle: `ZEILENBOX_TABELLE`, Karte: die Typografie-Zeilenbox). Vorgabe
  // bleibt die Typografie — die Kartenform ist von der Tabellenregel nicht
  // beruehrt und misst unveraendert.
  const box = (variant) => zeilenHoehe ?? lineBoxHeight(theme, variant)
  return (
    <>
      {[...Array(nameLines)].map((_, i) => (
        <Skeleton key={`name-${i}`} variant="text" width={i === 0 ? '70%' : '55%'}
          sx={{ height: box('body2') }} />
      ))}
      <Skeleton variant="text" width="50%" sx={{ height: box('caption') }} />
      <Skeleton variant="text" width="40%" sx={{ height: box('caption') }} />
    </>
  )
}

// Der INHALT einer KPI-Kachel — die zwei Balken der geladenen Kachel
// (Beschriftung + Wert). Die Kachel SELBST (Rahmen, Innenabstand, gestrichelter
// Teiler) kommt aus `KpiRow`/`KpiCell`, nicht von hier: eine eigene `Box` war
// die zweite Form derselben Sache (R15 B2, 2026-10-08).
function KpiCellSkeleton() {
  return (
    <>
      <Skeleton width="55%" height={14} sx={{ mb: 0.75 }} />
      <Skeleton width="70%" height={28} />
    </>
  )
}

export default function IncomingInventorySkeleton({ columns = INCOMING_INVENTORY_DEFAULT_COLUMNS }) {
  const theme = useTheme()
  // Dieselbe Weiche wie die Seite (`IncomingInventory.jsx` `isMobile`): unter `sm`
  // rendert der GELADENE Zustand eine Kartenliste, keine Tabelle
  // (`IncomingInventory.jsx` `isMobile ? … MobileRecordCard …`; die frühere
  // `CardActionArea`-Liste ist mit G8b gelöscht). Ein Tabellenskelett zeigte dort
  // eine Form, die es nach dem Laden nicht gibt (R16 W3, Runde 4).
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // ── Die Breitenmechanik (§2.7a, G8b) ──────────────────────────────────────
  // 🔴 Das Gerüst rechnet DIESELBE Verteilung wie die geladene Seite, mit seiner
  // EIGENEN gemessenen Behälterbreite (`useContainerWidth`) und derselben
  // Spaltenquelle. Ohne das läse es feste Zahlen und die Tabelle spränge beim
  // Eintreffen der Daten (Plan §2.7a letzter Absatz). Der Rückfall VOR der
  // ersten Messung ist das Muster der Seite — `CONTENT_WIDTH.full` ist `null`,
  // also `reading` (1280), nie 0 (Hausform `POOverview.jsx:821`).
  const tabelleRef = useRef(null)
  const { width: behaelterBreite } = useContainerWidth(tabelleRef)
  const messBasis = behaelterBreite ?? CONTENT_WIDTH.full ?? CONTENT_WIDTH.reading
  // 🔴 R15 W3: stabile Identitaet — der Hook verlangt sie ausdruecklich
  // (`useColumnPriority` JSDoc), sonst laufen `visible`, `sichtbar` und `layout`
  // bei jedem Render neu. Dieselbe Memoisierung wie in der Seite.
  const prioritaeten = useMemo(() => incomingInventoryPriorities(columns), [columns])
  const { visible } = useColumnPriority({
    columns: prioritaeten,
    containerWidth: messBasis,
  })
  const sichtbar = useMemo(() => new Set(visible.map((c) => c.key)), [visible])
  const sichtbareSpalten = useMemo(
    () => columns.filter((c) => sichtbar.has(c.field)),
    [columns, sichtbar]
  )
  const layout = incomingInventoryLayout(sichtbareSpalten, messBasis)

  return (
    /* R16 H1 (Wiedervorlage 1): `aria-label` an einem rollenlosen `<div>` wird von
       Screenreadern oft nicht vorgelesen. `role="status"` gibt dem Behaelter eine Rolle
       (implizit `aria-live="polite"`). Das ist hier richtig, weil DIESER Behälter
       eigenstaendig ist: er wird nur im Ladezustand gerendert und verschwindet mit ihm —
       es gibt nichts, was die Live-Region nachtraeglich ankuendigen wuerde.
       🔴 NICHT auf `RMAInbox.jsx` uebertragen: dort traegt DERSELBE Behälter auch die
       geladene Tabelle; eine Live-Region wuerde jede Tabellenaenderung ansagen
       (Sortieren, Seitenwechsel, neue Zeile). Begruendung steht dort im Kommentar. */
    <Box role="status" aria-busy="true" aria-label="Loading incoming inventory">
      {/* 🔴 R16 C1 (2026-10-06): KEIN Kopf-Platzhalter mehr. Den Kopf rendert die
          Seite selbst in JEDEM Zustand (`IncomingInventory.jsx`, `const kopf`) — der
          graue Balken hier war der Grund, warum im Laden GAR KEIN Titel stand (die
          mobile Leiste schweigt seit 5(i)). Das Skelett zeichnet nur den Inhalt. */}
      {/* 🔴 G8b (§2.7): dasselbe Muster wie die geladene Seite (`full`) und
          derselbe Rand (`PAGE_GUTTER`, den `PageContainer` setzt). Vorher stand
          hier `{ xs: 2, sm: 3 }` — auf dem Telefon 4 px mehr als geladen. */}
      <Box sx={{ px: PAGE_GUTTER, pb: 6 }}>
        <Stack spacing={3}>
          {/* 🔴 R15 B2 / Befund 9a (2026-10-08) hatte hier ZWEI Reihen (4 + 3)
              begruendet — dieselbe Aufteilung wie die geladene Seite damals.
              G8a (§2.5/B1) hat daraus EINE Reihe mit sechs Kacheln gemacht; der
              Absatz darunter ist der geltende, dieser ist die Aufzeichnung. */}
          {/* 🔴 UI2 Teil 10 G8a (§2.5/B1): EINE `KpiRow` mit SECHS Kacheln — die
              geladene Seite führt genau eine (§2.5). Zwei Reihen (4 + 3) zeichneten
              eine Form, die es nach dem Laden nicht mehr gibt, und die KPI-Höhe
              sprang beim Eintreffen. */}
          <KpiRow>
            {[...Array(6)].map((_, i) => (
              <KpiCell key={i}><KpiCellSkeleton /></KpiCell>
            ))}
          </KpiRow>

          {/* Table panel — dieselbe Bauform wie die geladene Fläche:
              Panel pad="none" + Toolbar + Inhalt */}
          <Panel pad="none">
            {/* Toolbar */}
            <Box sx={{
              p: SP.card, display: 'flex', gap: SP.sm, flexWrap: 'wrap',
              borderBottom: '1px solid', borderColor: 'divider',
            }}>
              <Skeleton variant="rectangular" width={200} height={36} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 1 }} />
            </Box>

            {/* 🔴 R16 B3 (2026-10-22): DERSELBE `px: SP.card`-Kasten wie die
                geladene Seite. Dort sitzt der Inhalt in
                `<Box sx={{ px: SP.card, … }}>` (`IncomingInventory.jsx:1018`), also
                Tabelle UND Fußzeile 2 × 16 px eingerückt; das Gerüst maß seinen
                Rollbehälter ohne dieses Polster und war 32 px breiter. Folge:
                die „ETA"-Kopfspalte war 153,1 statt 148,7 px (I.4 rot, 4,4 px),
                und an der Fit-Grenze konnte das Gerüst eine Spalte MEHR zeigen
                als die geladene Tabelle. Die Messung (`tabelleRef`) bleibt am
                inneren Rollbehälter — gemessen wird der RAUM, nicht der Deckel
                (BW4). */}
            <Box sx={{ px: SP.card }}>
            {isMobile ? (
              /* ── Mobil: Kartenskelett statt Tabelle ─────────────────────────
                 🔴 R16 W3 (2026-10-22): der geladene Zustand rendert unter `sm`
                 KEINE `CardActionArea`-Liste mehr — das war die Form VOR G8b.
                 Er rendert seit §2.9 je Lieferant einen STATISCHEN Gruppenkopf
                 (Name + „N items" + „N POs", kein Schalter) und darunter
                 `MobileRecordCard` (Titel zwei Zeilen + Variante, SKU-Kennung,
                 Chips, drei Metazeilen). Das Gerüst zeichnet dieselbe Anatomie;
                 die alte Form liess die Liste beim Laden sichtbar umspringen
                 (Kartenzahl, Höhe, fehlende Köpfe). Der frühere Kommentar
                 behauptete die gelöschte Form als geladenen Zustand.
                 KEIN Bildlaufbehälter: die Kartenliste scrollt nicht waagerecht. */
              <Stack spacing={0} data-testid="incoming-skeleton-cards">
                {/* Gruppenkopf — dieselbe Form wie geladen (`px: SP.card`,
                    `py: SP.tight`, `action.hover`, Rahmen oben und unten), nur
                    ohne Auf-/Zuklapp-Knopf: mobil sind die Köpfe statisch. */}
                <Box
                  aria-hidden="true"
                  sx={{
                    px: SP.card, py: SP.tight, bgcolor: 'action.hover',
                    borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider',
                  }}
                >
                  <Stack direction="row" spacing={SP.chip} alignItems="center">
                    <Skeleton variant="text" width={140} sx={{ height: lineBoxHeight(theme, 'subtitle2') }} />
                    <Skeleton variant="rounded" width={56} height={pillHoeheSm(theme)} />
                    <Skeleton variant="rounded" width={40} height={pillHoeheSm(theme)} />
                  </Stack>
                </Box>
                {[...Array(ROWS)].map((_, i) => (
                  <Panel
                    key={i}
                    aria-hidden="true"
                    // 🔴 R16 W1 (2026-10-22): DIESELBE HUELLE wie die geladene
                    // Karte. `MobileRecordCard` rendert `Panel pad="none"
                    // shadow="none" sx={{ p: SP.tight }}` (`:491-505`), und die
                    // Karten stehen mit `Stack spacing={0}` direkt aneinander.
                    // Vorher zeichnete das Geruest `p: SP.tight` +
                    // `borderBottom: 1px` — beim Laden wechselte damit der
                    // Umriss sichtbar von Trennzeile zu Rahmen (1 px je Karte).
                    pad="none"
                    shadow="none"
                    sx={{ p: SP.tight }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      {/* Titel (zwei Namenszeilen + Variante) und SKU-Kennung */}
                      <FirstCellSkeleton theme={theme} nameLines={2} />
                      {/* Chips der Karte (PO-Kennung, ggf. „At risk") */}
                      <Stack direction="row" spacing={SP.chip} sx={{ mt: SP.chip }}>
                        <Skeleton variant="rounded" width={56} height={pillHoeheSm(theme)} />
                      </Stack>
                      {/* drei Metazeilen: ETA / Inbound / Est. Stockout */}
                      <Box sx={{ mt: SP.chip }}>
                        {[...Array(3)].map((__, m) => (
                          <Skeleton key={m} variant="text" width={m === 0 ? '55%' : '65%'}
                            sx={{ height: lineBoxHeight(theme, 'caption') }} />
                        ))}
                      </Box>
                    </Box>
                  </Panel>
                ))}
              </Stack>
            ) : (
              /* ── Desktop: Gruppenkopf + Tabelle ────────────────────────────
                 In DERSELBEN Bildlaufhülle wie der geladene Zustand
                 (`IncomingInventory.jsx` `<Box overflow="auto">`): die Kopfzellen
                 tragen feste Etikettenbreiten, das Skelett läuft bei 390 px deshalb
                 über. Vor PR 1 waren die Balken vollbreit — der Überlauf ist neu.
                 `clip` am Panel wäre die Alternative, spiegelt den geladenen Zustand
                 aber nicht (R15 B2 / R16 W2). */
              <Box ref={tabelleRef} overflow="auto" data-testid="incoming-skeleton-scroll">
                {/* Gruppenkopf — der geladene Vorgabezustand (`groupBy='vendor'`)
                    rendert JE GRUPPE einen Kopf über der eigenen `<Table size="small">`.
                    Ohne ihn fehlt im Ladezustand eine Zeile, und der Inhalt springt
                    beim Eintreffen der Daten (R15 W1, Runde 4). Maße aus denselben
                    Token wie geladen: `px: SP.card` / `py: SP.tight` = `px: 2` / `py: 1`,
                    `bgcolor: action.hover`, Rahmen oben und unten. */}
                <Box
                  aria-hidden="true"
                  sx={{
                    px: SP.card, py: SP.tight, bgcolor: 'action.hover',
                    borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider',
                  }}
                >
                  <Stack direction="row" spacing={SP.chip} alignItems="center">
                    {/* der Auf-/Zuklapp-Knopf der geladenen Gruppe (24 px) */}
                    <Skeleton variant="circular" width={24} height={24} />
                    <Skeleton variant="text" width={140} sx={{ height: lineBoxHeight(theme, 'subtitle2') }} />
                    {/* die Anzahl-Pille der geladenen Gruppe (`Pill size="sm"`) */}
                    <Skeleton variant="rounded" width={56} height={pillHoeheSm(theme)} />
                  </Stack>
                </Box>

                <Table
                  size="small"
                  sx={{
                    tableLayout: 'fixed',
                    // Die Rollschwelle aus derselben Rechnung wie geladen (§2.7a).
                    minWidth: incomingInventoryMinWidth(sichtbareSpalten),
                    // Das ANWENDEN des Deckels `min(Behälter, Budget)`. Am
                    // `Table` statt an einem zweiten `TableContainer`: der
                    // gemessene Träger (`incoming-skeleton-scroll`) darf seinen
                    // eigenen Deckel NICHT tragen, sonst misst der Beobachter
                    // den Deckel und nicht den Raum.
                    ...(layout.tabellenMax > 0 ? { maxWidth: layout.tabellenMax } : {}),
                  }}
                >
                  <TableHead>
                    {/* auch die Kopfzeile ist Ladezustand, nicht Inhalt (R16 W3) */}
                    <TableRow aria-hidden="true">
                      {sichtbareSpalten.map((c) => (
                        <TableCell key={c.field} align={c.align} sx={{ width: layout.widthFor(c.field) }}>
                          {/* Dieselben Etiketten wie der geladene Kopf, in DERSELBEN
                              Box (`TableSortLabel`) — die Kopfhöhe folgt daraus, statt
                              aus einer abgestimmten Zahl. `disabled` ⇒ nicht
                              fokussierbar, kein Sortieren, kein Hover
                              (R15 W1 / R16 W1, Runde 4). */}
                          <TableSortLabel disabled>{c.label}</TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...Array(ROWS)].map((_, r) => (
                      <TableRow key={r} aria-hidden="true">
                        {sichtbareSpalten.map((c, i) => (
                          <TableCell key={c.field} align={c.align} sx={{ width: layout.widthFor(c.field) }}>
                            {i === 0 ? (
                              <FirstCellSkeleton theme={theme} nameLines={2} zeilenHoehe={ZEILENBOX_TABELLE} />
                            ) : (
                              <Skeleton variant="text" width="60%" sx={{ height: ZEILENBOX_TABELLE }} />
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
            </Box>
          </Panel>

          {/* Kalender-Falte (G8a §2.8): geladen steht der Kalender eingeklappt
              UNTER dem Daten-Panel. Das Gerüst zeichnet darum keine 220-px-Fläche
              mehr, sondern eine Zeile in Faltenhöhe — sonst springt die Seite beim
              Eintreffen um die Kalenderhöhe. */}
          <Panel pad="sm">
            <Skeleton variant="text" width={180} sx={{ height: lineBoxHeight(theme, 'h6') }} />
          </Panel>
        </Stack>
      </Box>
    </Box>
  )
}
